// analytics.js — lightweight Google Analytics 4 (gtag.js) integration.
//
// GUARDED, exactly like App Check: this is a complete no-op unless
// VITE_GA_MEASUREMENT_ID (a GA4 measurement ID, e.g. "G-XXXXXXXXXX") is set.
// With no ID configured the gtag script is never injected, so it's safe to ship
// before Google Analytics exists. Paste the ID into .env.local to switch it on.
//
// We disable gtag's automatic page_view and send views manually on route change
// (see RouteAnalytics in App.jsx) because this is a client-side-routed SPA — the
// browser only performs one real navigation, so auto page_view would report a
// single hit for the entire session.
//
// Localhost is excluded by default so your own dev traffic doesn't pollute stats.

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

let initialized = false;

function isTrackableHost() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  // Skip local development; everything else (giraops.com, *.web.app) is real.
  return h !== 'localhost' && h !== '127.0.0.1' && h !== '';
}

// Inject gtag.js and configure the GA4 stream. Safe to call repeatedly — only the
// first call with a configured ID on a real host does anything.
export function initAnalytics() {
  if (initialized || !GA_ID || !isTrackableHost()) return;
  initialized = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // gtag must push the literal `arguments` object — do NOT refactor to (...args).
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  // send_page_view:false — we fire page_view ourselves on every route change.
  window.gtag('config', GA_ID, { send_page_view: false });
}

// Manual SPA page view. `path` should be pathname + search (no origin).
export function trackPageView(path) {
  if (!initialized || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

// Generic event helper (e.g. trackEvent('sign_up', { method: 'email' })).
export function trackEvent(name, params = {}) {
  if (!initialized || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
}
